import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef, ColumnState, ModuleRegistry, ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, DateEditorModule, RowAutoHeightModule, GridReadyEvent, GridApi, ColumnResizedEvent, ColumnMovedEvent, ColumnVisibleEvent, FilterChangedEvent } from 'ag-grid-community';
import { Box, Paper, useTheme, Popover, TextField, InputAdornment, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, IconButton, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { Search as SearchIcon, FileDownload as FileDownloadIcon, Add as AddIcon, Close as CloseIcon, ViewColumn as ViewColumnIcon, UnfoldMore as UnfoldMoreIcon, UnfoldLess as UnfoldLessIcon, KeyboardArrowRight, KeyboardArrowDown, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from '@mui/icons-material';
import { MDRData, WorkflowStep, Party, RevisionName, Project } from '../types';
import { StatusChip, WorkflowSteps } from './MDRComponents';
import * as XLSX from 'xlsx';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { localStore } from '../utils/localStore';
import { TABLE_FONT_FAMILY } from '../theme';
import { FileCategoryChip } from './FileCategoryChip';
import { ClearGridFiltersButton, useColumnFiltersActive } from './ClearGridFiltersButton';
import { compareMdrRowsByExpectedDate, toExpectedDateSortKey } from '../utils/mdrExpectedDateSort';

// Register AG Grid modules
ModuleRegistry.registerModules([ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, DateEditorModule, RowAutoHeightModule]);

const PROGRAMMATIC_COLUMN_RESIZE_SOURCES = new Set([
    'api',
    'sizeColumnsToFit',
    'autosizeColumns',
    'gridOptionsChanged',
    'flex',
]);

const MDR_TABLE_STATE_KEY = 'mdr_table_state_v2';
const MDR_TABLE_COLUMN_WIDTHS_KEY = 'mdr_table_column_widths_v1';

interface MDRTablePersistedState {
    columnState?: ColumnState[];
    columnGroupState?: Array<{ groupId: string; open: boolean }>;
    filterModel?: Record<string, unknown>;
    quickFilter?: string;
    selectedProjectId?: number | 'all';
    expandedDocIds?: number[];
    expandedRevIds?: string[];
    zoomLevel?: number;
}

const MDR_TABLE_CELL_PADDING = 28;

function measureMonospaceWidth(text: string, fontWeight: number, fontSizeRem: number): number {
    const label = text || '';
    if (typeof document === 'undefined') {
        return label.length * 8.4 + MDR_TABLE_CELL_PADDING;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return label.length * 8.4 + MDR_TABLE_CELL_PADDING;
    }

    const fontSizePx = fontSizeRem * 16;
    const primaryFont = TABLE_FONT_FAMILY.split(',')[0].replace(/"/g, "'");
    ctx.font = `${fontWeight} ${fontSizePx}px ${primaryFont}, monospace`;
    return ctx.measureText(label).width + MDR_TABLE_CELL_PADDING;
}

// Import AG Grid styles
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import RevisionWithWorkflowDialog from './RevisionWithWorkflowDialog';
import { CreateDocumentRevisionForm } from '../types';
import { getNextRevisionCounter } from '../utils/revisionUtils';

/** Vertically center custom cell renderers within fixed-height rows */
const verticallyCenteredCellStyle = {
    display: 'flex',
    alignItems: 'center',
    lineHeight: 'normal',
} as const;

const CellCenter = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
        {children}
    </Box>
);

interface MDRTableAgGridProps {
    data: MDRData[];
    projects: Project[];
    revisionNames: RevisionName[];
    parties: Party[];
    onUpdateDate: (docId: number, revId: number, date: string | null) => Promise<void>;

    onAddRevision: (docId: number, data: any) => Promise<{ revision_id: number } | void>;
    onRowClick?: (data: MDRData | null) => void;
    onRowDoubleClick?: (data: MDRData) => void;
    onDownloadLatest?: (docId: number, revisionId: number | undefined, lastRevision: string) => Promise<void>;
    showProjectFilter?: boolean;
    viewMode?: 'simple' | 'resources';
    revisionFiles?: Record<string, any[]>;
    onExpandRevision?: (docId: number, revId: number) => void;
    onDownloadFile?: (docId: number, revId: number, versionId: number, fileId: number, filename: string) => Promise<void>;
}

interface GridRow {
    id: string;
    type: 'document' | 'revision' | 'file';
    document_id: number;
    document_nd_number?: string;
    document_title?: string;
    document_type_name?: string;
    work_package_code?: string;
    work_unit_code?: string;
    revision_id?: number;
    revision_name_step?: string;
    status?: string;
    expected_issue_date?: string | null;
    issued_date?: string | null;
    revision_counter?: number;
    workflow?: WorkflowStep[];
    document_is_cancelled?: boolean;

    // File specific
    file_id?: number;
    file_version_id?: number;
    file_name?: string;
    file_category?: string;
    file_size?: number;
    file_type?: string;
}

const MDRTableAgGrid: React.FC<MDRTableAgGridProps> = ({
    data,
    projects,
    revisionNames,
    parties,
    onUpdateDate,

    onAddRevision,
    onRowClick,
    onRowDoubleClick,
    onDownloadLatest,
    showProjectFilter = true,
    viewMode = 'simple',
    revisionFiles = {},
    onExpandRevision,
    onDownloadFile
}) => {
    const theme = useTheme();
    const [quickFilter, setQuickFilter] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState<number | 'all'>('all');
    const [zoomLevel, setZoomLevel] = useState(0.85);

    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const { columnFiltersActive, syncColumnFilters, clearColumnFilters } = useColumnFiltersActive();
    const [colVisAnchorEl, setColVisAnchorEl] = useState<HTMLElement | null>(null);
    const [allColumns, setAllColumns] = useState<any[]>([]);

    // Expansion State
    const [expandedDocIds, setExpandedDocIds] = useState<Set<number>>(new Set());
    const [expandedRevIds, setExpandedRevIds] = useState<Set<string>>(new Set()); // "docId-revId"

    const hasSavedColumnWidthsRef = useRef(false);
    const savedTableStateRef = useRef<MDRTablePersistedState | null>(null);
    const savedStateLoadedRef = useRef(false);
    const restoreCompleteRef = useRef(false);
    const isApplyingSavedStateRef = useRef(false);
    const [savedStateVersion, setSavedStateVersion] = useState(0);
    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applySavedColumnStateToApi = useCallback((api: GridApi, applyOrder = false) => {
        const savedState = savedTableStateRef.current?.columnState;
        if (!savedState?.length) return;

        const state = savedState.map((columnState) => ({
            ...columnState,
            flex: columnState.flex ?? null,
        }));

        isApplyingSavedStateRef.current = true;
        try {
            api.applyColumnState({ state, applyOrder });
            hasSavedColumnWidthsRef.current = savedState.some(
                (columnState) => typeof columnState.width === 'number' && columnState.width > 0
            );
        } finally {
            window.setTimeout(() => {
                isApplyingSavedStateRef.current = false;
            }, 0);
        }
    }, []);

    const applySavedGridStateToApi = useCallback((api: GridApi) => {
        applySavedColumnStateToApi(api, true);

        const savedState = savedTableStateRef.current;
        if (savedState?.columnGroupState?.length) {
            api.setColumnGroupState(savedState.columnGroupState);
        }
        if (savedState?.filterModel && Object.keys(savedState.filterModel).length) {
            api.setFilterModel(savedState.filterModel);
        }
    }, [applySavedColumnStateToApi]);

    const loadPersistedTableState = useCallback(async (): Promise<MDRTablePersistedState | null> => {
        try {
            let savedState = await localStore.get(MDR_TABLE_STATE_KEY) as MDRTablePersistedState | undefined;
            if (!savedState || typeof savedState !== 'object') {
                const legacyWidths = await localStore.get(MDR_TABLE_COLUMN_WIDTHS_KEY) as Record<string, number> | undefined;
                if (legacyWidths && typeof legacyWidths === 'object') {
                    const columnState = Object.entries(legacyWidths)
                        .filter(([, width]) => typeof width === 'number' && width > 0)
                        .map(([colId, width]) => ({ colId, width, flex: null }));
                    if (columnState.length) {
                        savedState = { columnState };
                    }
                }
            }
            return savedState ?? null;
        } catch {
            return null;
        }
    }, []);

    const collectTableState = useCallback((api: GridApi | null): MDRTablePersistedState => ({
        columnState: api?.getColumnState() ?? savedTableStateRef.current?.columnState,
        columnGroupState: api?.getColumnGroupState() ?? savedTableStateRef.current?.columnGroupState,
        filterModel: api?.getFilterModel() ?? savedTableStateRef.current?.filterModel,
        quickFilter,
        selectedProjectId,
        expandedDocIds: Array.from(expandedDocIds),
        expandedRevIds: Array.from(expandedRevIds),
        zoomLevel,
    }), [quickFilter, selectedProjectId, expandedDocIds, expandedRevIds, zoomLevel]);

    const persistTableState = useCallback((api: GridApi | null = gridApi) => {
        if (!restoreCompleteRef.current || !api) {
            return;
        }

        if (persistTimeoutRef.current) {
            clearTimeout(persistTimeoutRef.current);
        }

        persistTimeoutRef.current = setTimeout(async () => {
            try {
                const state = collectTableState(api);
                savedTableStateRef.current = state;
                await localStore.set(MDR_TABLE_STATE_KEY, state);
                hasSavedColumnWidthsRef.current = Boolean(
                    state.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
                );
            } catch {
                // Ignore persistence failures (private mode / blocked storage).
            }
            persistTimeoutRef.current = null;
        }, 400);
    }, [collectTableState, gridApi]);

    const contentColumnWidthsRef = useRef({ ndNumber: 150, title: 180 });
    const gridResizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyContentColumnWidths = useCallback((api: GridApi) => {
        const savedColumnState = savedTableStateRef.current?.columnState;
        const savedWidths = savedColumnState?.reduce<Record<string, number>>((acc, columnState) => {
            if (columnState.colId && typeof columnState.width === 'number' && columnState.width > 0) {
                acc[columnState.colId] = columnState.width;
            }
            return acc;
        }, {});
        const { ndNumber, title } = contentColumnWidthsRef.current;
        const updates: { key: string; newWidth: number }[] = [];

        if (!savedWidths?.nd_number) {
            updates.push({ key: 'nd_number', newWidth: ndNumber });
        }
        if (!savedWidths?.title) {
            updates.push({ key: 'title', newWidth: title });
        }

        if (updates.length) {
            api.setColumnWidths(updates);
        }
    }, []);

    const fitFlexibleColumnsToViewport = useCallback((api: GridApi) => {
        window.requestAnimationFrame(() => {
            try {
                applyContentColumnWidths(api);
                if (!hasSavedColumnWidthsRef.current) {
                    api.sizeColumnsToFit();
                }
            } catch {
                // Ignore fit calls during transient grid rebuilds.
            }
        });
    }, [applyContentColumnWidths]);

    const scheduleGridResizeLayout = useCallback((api: GridApi) => {
        applyContentColumnWidths(api);
        if (gridResizeTimeoutRef.current) {
            clearTimeout(gridResizeTimeoutRef.current);
        }
        gridResizeTimeoutRef.current = setTimeout(() => {
            if (!hasSavedColumnWidthsRef.current) {
                try {
                    api.sizeColumnsToFit();
                } catch {
                    // Ignore fit calls during transient grid rebuilds.
                }
            }
            gridResizeTimeoutRef.current = null;
        }, 300);
    }, [applyContentColumnWidths]);

    useEffect(() => () => {
        if (gridResizeTimeoutRef.current) {
            clearTimeout(gridResizeTimeoutRef.current);
        }
        if (persistTimeoutRef.current) {
            clearTimeout(persistTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        void loadPersistedTableState().then((savedState) => {
            if (cancelled || !savedState) {
                savedStateLoadedRef.current = true;
                setSavedStateVersion((v) => v + 1);
                return;
            }

            savedTableStateRef.current = savedState;

            if (savedState.quickFilter !== undefined) {
                setQuickFilter(savedState.quickFilter);
            }
            if (savedState.selectedProjectId !== undefined) {
                setSelectedProjectId(savedState.selectedProjectId);
            }
            if (typeof savedState.zoomLevel === 'number') {
                setZoomLevel(savedState.zoomLevel);
            }
            if (savedState.expandedDocIds?.length) {
                setExpandedDocIds(new Set(savedState.expandedDocIds));
            }
            if (savedState.expandedRevIds?.length) {
                setExpandedRevIds(new Set(savedState.expandedRevIds));
            }

            hasSavedColumnWidthsRef.current = Boolean(
                savedState.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
            );
            savedStateLoadedRef.current = true;
            setSavedStateVersion((v) => v + 1);
        });

        return () => {
            cancelled = true;
        };
    }, [loadPersistedTableState]);

    useEffect(() => {
        if (!restoreCompleteRef.current) {
            return;
        }
        persistTableState();
    }, [quickFilter, selectedProjectId, zoomLevel, expandedDocIds, expandedRevIds, persistTableState]);

    useEffect(() => {
        if (viewMode !== 'resources' || !onExpandRevision) {
            return;
        }

        expandedRevIds.forEach((revKey) => {
            if (revisionFiles[revKey]) {
                return;
            }
            const [docIdStr, revIdStr] = revKey.split('-');
            const docId = Number(docIdStr);
            const revId = Number(revIdStr);
            if (Number.isFinite(docId) && Number.isFinite(revId)) {
                onExpandRevision(docId, revId);
            }
        });
    }, [expandedRevIds, viewMode, onExpandRevision, revisionFiles]);

    const onGridReady = useCallback(async (params: GridReadyEvent) => {
        try {
            if (!savedStateLoadedRef.current) {
                const savedState = await loadPersistedTableState();
                if (savedState) {
                    savedTableStateRef.current = savedState;
                    hasSavedColumnWidthsRef.current = Boolean(
                        savedState.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
                    );
                }
            }

            applySavedGridStateToApi(params.api);
            fitFlexibleColumnsToViewport(params.api);
        } finally {
            savedStateLoadedRef.current = true;
            restoreCompleteRef.current = true;
            setSavedStateVersion((v) => v + 1);
            setGridApi(params.api);
            syncColumnFilters(params.api);
        }
    }, [applySavedGridStateToApi, fitFlexibleColumnsToViewport, loadPersistedTableState, syncColumnFilters]);

    const toggleColumn = (colId: string, visible: boolean) => {
        if (gridApi) {
            gridApi.setColumnsVisible([colId], visible);
            setAllColumns(prev => prev.map(c =>
                c.colId === colId ? { ...c, visible } : c
            ));
            fitFlexibleColumnsToViewport(gridApi);
            persistTableState(gridApi);
        }
    };

    const toggleExpand = (docId: number) => {
        setExpandedDocIds(prev => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    };

    const toggleRevExpand = (docId: number, revId: number) => {
        const key = `${docId}-${revId}`;
        setExpandedRevIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
                if (onExpandRevision && (!revisionFiles || !revisionFiles[key])) {
                    onExpandRevision(docId, revId);
                }
            }
            return next;
        });
    };

    const expandAll = () => {
        const allIds = data.map(d => d.document_id);
        setExpandedDocIds(new Set(allIds));
    };

    const collapseAll = () => {
        setExpandedDocIds(new Set());
        setExpandedRevIds(new Set());
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 1.5));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 0.7));
    };

    // Add Revision State
    const [addRevDialogOpen, setAddRevDialogOpen] = useState(false);
    const [addRevDocId, setAddRevDocId] = useState<number | null>(null);

    const openAddRevisionDialog = (docId: number) => {
        setAddRevDocId(docId);
        setAddRevDialogOpen(true);
    };

    const handleAddRevisionSave = async (form: CreateDocumentRevisionForm) => {
        if (addRevDocId) {
            return onAddRevision(addRevDocId, form);
        }
    };

    // Get document title for dialog
    const selectedDocForRev = useMemo(() => {
        return data.find(d => d.document_id === addRevDocId);
    }, [data, addRevDocId]);

    // Filter data based on quick filter and project selection
    const filteredData = useMemo(() => {
        let processedData = [...data];

        // Filter by project first
        if (selectedProjectId !== 'all') {
            processedData = processedData.filter(doc => doc.project_id === selectedProjectId);
        }

        if (quickFilter) {
            const lowerFilter = quickFilter.toLowerCase();
            processedData = processedData.map(doc => {
                // Check if document matches
                const docMatches = (
                    (doc.document_nd_number?.toLowerCase().includes(lowerFilter)) ||
                    (doc.document_title?.toLowerCase().includes(lowerFilter)) ||
                    (doc.document_type_name?.toLowerCase().includes(lowerFilter)) ||
                    (doc.work_package_code?.toLowerCase().includes(lowerFilter)) ||
                    (doc.work_unit_code?.toLowerCase().includes(lowerFilter))
                );

                if (docMatches) {
                    // If document matches, return document with ALL revisions
                    return doc;
                }

                // Check if any revision matches
                const matchingRevisions = doc.revisions.filter(rev => {
                    return (
                        (rev.revision_name_step?.toLowerCase().includes(lowerFilter)) ||
                        (rev.status?.toLowerCase().includes(lowerFilter)) ||
                        (rev.expected_issue_date?.toLowerCase().includes(lowerFilter)) ||
                        (rev.issued_date?.toLowerCase().includes(lowerFilter)) ||
                        // Check workflow steps
                        (rev.workflow?.some(step =>
                            step.party_trigram?.toLowerCase().includes(lowerFilter) ||
                            step.role?.toLowerCase().includes(lowerFilter)
                        ))
                    );
                });

                if (matchingRevisions.length > 0) {
                    // If revisions match, return document with ONLY matching revisions
                    return { ...doc, revisions: matchingRevisions };
                }

                return null;
            }).filter(doc => doc !== null) as MDRData[];
        }

        // Sort by Work Package then Work Unit
        return processedData.sort((a, b) => {
            const wpA = a.work_package_code || '';
            const wpB = b.work_package_code || '';
            if (wpA !== wpB) return wpA.localeCompare(wpB);

            const wuA = a.work_unit_code || '';
            const wuB = b.work_unit_code || '';
            return wuA.localeCompare(wuB);
        });
    }, [data, quickFilter, selectedProjectId]);

    const contentColumnWidths = useMemo(() => {
        const fontSizeRem = zoomLevel;
        let ndWidth = measureMonospaceWidth('ND Number', 600, fontSizeRem);
        let titleWidth = measureMonospaceWidth('Title', 600, fontSizeRem);

        filteredData.forEach((doc) => {
            if (doc.document_nd_number) {
                ndWidth = Math.max(ndWidth, measureMonospaceWidth(doc.document_nd_number, 700, fontSizeRem));
            }
            if (doc.document_title) {
                titleWidth = Math.max(titleWidth, measureMonospaceWidth(doc.document_title, 400, fontSizeRem));
            }

            if (expandedDocIds.has(doc.document_id)) {
                doc.revisions.forEach((rev) => {
                    const revKey = `${doc.document_id}-${rev.revision_id}`;
                    const files = revisionFiles[revKey] || [];
                    files.forEach((file) => {
                        const name = file.filename || file.name || '';
                        titleWidth = Math.max(
                            titleWidth,
                            measureMonospaceWidth(name, 400, fontSizeRem) + 56
                        );
                    });
                });
            }
        });

        return {
            ndNumber: Math.ceil(Math.max(150, ndWidth)),
            title: Math.ceil(Math.max(180, titleWidth)),
        };
    }, [filteredData, expandedDocIds, revisionFiles, zoomLevel]);

    contentColumnWidthsRef.current = contentColumnWidths;

    // Custom comparator to maintain hierarchy
    const hierarchyComparator = useCallback((valueA: any, valueB: any, nodeA: any, nodeB: any, isDescending: boolean) => {
        const docIdA = nodeA.data.document_id;
        const docIdB = nodeB.data.document_id;

        if (docIdA === docIdB) {
            // Same document family.

            // If revisions match
            const revIdA = nodeA.data.revision_id;
            const revIdB = nodeB.data.revision_id;

            if (revIdA && revIdB && revIdA === revIdB) {
                // Same revision. Revision first (type='revision'), then files.
                if (nodeA.data.type === 'revision') return isDescending ? 1 : -1;
                if (nodeB.data.type === 'revision') return isDescending ? -1 : 1;
                return 0; // Both files
            }

            if (nodeA.data.type === 'document') return isDescending ? 1 : -1;
            if (nodeB.data.type === 'document') return isDescending ? -1 : 1;

            return 0;
        }

        // Different documents. Compare the values.
        if (valueA === valueB) return 0;
        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;

        return valueA > valueB ? 1 : -1;
    }, []);

    // Helper to get last revision
    const getLastRevision = useCallback((revisions: any[]) => {
        if (!revisions || revisions.length === 0) return null;
        return [...revisions].sort((a, b) => {
            const counterA = a.revision_counter ?? -1;
            const counterB = b.revision_counter ?? -1;
            if (counterA !== counterB) return counterB - counterA;
            return b.revision_id - a.revision_id;
        })[0];
    }, []);

    const documentExpectedDateById = useMemo(() => {
        const map = new Map<number, number | null>();
        filteredData.forEach((doc) => {
            const lastRev = getLastRevision(doc.revisions);
            map.set(doc.document_id, toExpectedDateSortKey(lastRev?.expected_issue_date));
        });
        return map;
    }, [filteredData, getLastRevision]);
    const documentExpectedDateByIdRef = useRef(documentExpectedDateById);
    documentExpectedDateByIdRef.current = documentExpectedDateById;

    const expectedDateComparator = useCallback((
        _valueA: unknown,
        _valueB: unknown,
        nodeA: { data?: GridRow },
        nodeB: { data?: GridRow },
        isDescending: boolean,
    ) => compareMdrRowsByExpectedDate(
        nodeA?.data,
        nodeB?.data,
        documentExpectedDateByIdRef.current,
        isDescending,
    ), []);

    // Flatten data structure
    const rowData = useMemo<GridRow[]>(() => {
        const rows: GridRow[] = [];
        filteredData.forEach(doc => {
            const isExpanded = expandedDocIds.has(doc.document_id);
            const lastRev = isExpanded ? null : getLastRevision(doc.revisions);

            const docRow: GridRow = {
                id: `doc-${doc.document_id}`,
                type: 'document',
                document_id: doc.document_id,
                document_nd_number: doc.document_nd_number,
                document_title: doc.document_title,
                document_type_name: doc.document_type_name,
                work_package_code: doc.work_package_code,
                work_unit_code: doc.work_unit_code,

                // If collapsed, populate with summary data from last revision
                revision_id: lastRev?.revision_id,
                revision_name_step: lastRev?.revision_name_step,
                status: lastRev?.status,
                revision_counter: lastRev?.revision_counter,
                expected_issue_date: lastRev?.expected_issue_date,
                issued_date: lastRev?.issued_date,
                workflow: lastRev?.status?.toLowerCase() === 'approved' ? undefined : lastRev?.workflow,
                document_is_cancelled: !!doc.is_cancelled
            };
            rows.push(docRow);

            if (isExpanded) {
                doc.revisions.forEach(rev => {
                    rows.push({
                        id: `rev-${rev.revision_id}`,
                        type: 'revision',
                        document_id: doc.document_id,
                        document_nd_number: doc.document_nd_number,
                        document_title: doc.document_title,
                        document_type_name: doc.document_type_name,
                        work_package_code: doc.work_package_code,
                        work_unit_code: doc.work_unit_code,
                        revision_id: rev.revision_id,
                        revision_name_step: rev.revision_name_step,
                        status: rev.status,
                        revision_counter: rev.revision_counter,
                        expected_issue_date: rev.expected_issue_date,
                        issued_date: rev.issued_date,
                        workflow: rev.workflow,
                        document_is_cancelled: !!doc.is_cancelled
                    });

                    // Files expansion
                    const revKey = `${doc.document_id}-${rev.revision_id}`;
                    if (viewMode === 'resources' && expandedRevIds.has(revKey)) {
                        const files = revisionFiles[revKey] || [];
                        if (files.length === 0 && !revisionFiles[revKey]) {
                            // Loading state or empty? We can add a dummy row or just wait
                        }
                        files.forEach((file: any, index: number) => {
                            rows.push({
                                id: `file-${rev.revision_id}-${file.id || index}`,
                                type: 'file',
                                document_id: doc.document_id,
                                // Copy for sorting
                                document_nd_number: doc.document_nd_number,
                                // Use filename as title?
                                document_title: file.filename || file.name,
                                document_type_name: file.category || 'Attachment', // Reuse type column for file category
                                work_package_code: doc.work_package_code,
                                work_unit_code: doc.work_unit_code,

                                revision_id: rev.revision_id,
                                revision_name_step: rev.revision_name_step, // Group with revision
                                status: '', // Files don't have status usually

                                file_id: file.id,
                                file_version_id: file.version_id,
                                file_name: file.filename || file.name,
                                file_category: file.category,
                                file_size: file.size,
                                file_type: file.mimetype
                                ,
                                document_is_cancelled: !!doc.is_cancelled
                            });
                        });
                    }
                });
            }
        });
        return rows;
    }, [filteredData, expandedDocIds, expandedRevIds, getLastRevision, viewMode, revisionFiles]);

    const dateFilterParams = useMemo(() => ({
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (cellValue == null) return -1;
            const dateParts = cellValue.split('-');
            if (dateParts.length === 3) {
                const year = Number(dateParts[0]);
                const month = Number(dateParts[1]) - 1;
                const day = Number(dateParts[2]);
                const cellDate = new Date(year, month, day);
                cellDate.setHours(0, 0, 0, 0);

                if (cellDate.getTime() === filterLocalDateAtMidnight.getTime()) {
                    return 0;
                }
                if (cellDate < filterLocalDateAtMidnight) {
                    return -1;
                }
                if (cellDate > filterLocalDateAtMidnight) {
                    return 1;
                }
            }
            return 0;
        },
        browserDatePicker: true,
        minValidYear: 2000,
        maxValidYear: 2100,
    }), []);

    const columnDefs = useMemo<(ColDef<GridRow> | ColGroupDef<GridRow>)[]>(() => [
        {
            groupId: 'documentDetails',
            headerName: "Document Details",
            children: [
                {
                    colId: 'wp',
                    headerName: "WP",
                    field: "work_package_code",
                    width: 120,
                    pinned: 'left',
                    comparator: hierarchyComparator,
                    cellRenderer: (params: any) => {
                        if (params.data.type === 'document') {
                            const isExpanded = expandedDocIds.has(params.data.document_id);
                            return (
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand(params.data.document_id);
                                        }}
                                        sx={{ mr: 1 * zoomLevel, p: 0.5 * zoomLevel }}
                                    >
                                        {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 16 * zoomLevel }} /> : <KeyboardArrowRight sx={{ fontSize: 16 * zoomLevel }} />}
                                    </IconButton>
                                    {params.value}
                                </Box>
                            );
                        }
                        return params.value;
                    }
                },
                {
                    colId: 'wu',
                    headerName: "WU",
                    field: "work_unit_code",
                    width: 90,
                    pinned: 'left',
                    comparator: hierarchyComparator
                },
                {
                    colId: 'nd_number',
                    headerName: "ND Number",
                    field: "document_nd_number",
                    width: contentColumnWidths.ndNumber,
                    minWidth: contentColumnWidths.ndNumber,
                    pinned: 'left',
                    suppressSizeToFit: true,
                    cellStyle: { fontFamily: TABLE_FONT_FAMILY, overflow: 'hidden', fontSize: `${0.85 * zoomLevel}rem` },
                    cellRenderer: (params: any) => {
                        if (params.data.type === 'document') {
                            return (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: TABLE_FONT_FAMILY,
                                        fontWeight: 700,
                                        fontSize: `${0.85 * zoomLevel}rem`,
                                        whiteSpace: 'nowrap',
                                        textDecoration: params.data.document_is_cancelled ? 'line-through' : 'none',
                                        color: params.data.document_is_cancelled ? 'text.disabled' : 'inherit'
                                    }}
                                >
                                    {params.data.document_nd_number}
                                </Typography>
                            );
                        }
                        return null;
                    },
                    comparator: hierarchyComparator
                },
                {
                    colId: 'title',
                    headerName: "Title",
                    field: "document_title",
                    width: contentColumnWidths.title,
                    minWidth: contentColumnWidths.title,
                    pinned: 'left',
                    suppressSizeToFit: true,
                    cellStyle: { ...verticallyCenteredCellStyle, fontFamily: TABLE_FONT_FAMILY, overflow: 'hidden', fontSize: `${0.85 * zoomLevel}rem` },
                    cellRenderer: (params: any) => {
                        if (params.data.type === 'document') {
                            return (
                                <CellCenter>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: TABLE_FONT_FAMILY,
                                            textDecoration: params.data.document_is_cancelled ? 'line-through' : 'none',
                                            color: params.data.document_is_cancelled ? 'text.disabled' : 'inherit',
                                            fontWeight: params.data.document_is_cancelled ? 500 : 400,
                                            whiteSpace: 'nowrap',
                                            fontSize: `${0.85 * zoomLevel}rem`
                                        }}
                                    >
                                        {params.data.document_title}
                                    </Typography>
                                </CellCenter>
                            );
                        }
                        if (params.data.type === 'file') {
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 4, gap: 1, height: '100%' }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onDownloadFile && params.data.file_id && params.data.file_version_id) {
                                                onDownloadFile(
                                                    params.data.document_id,
                                                    params.data.revision_id!,
                                                    params.data.file_version_id,
                                                    params.data.file_id,
                                                    params.data.file_name!
                                                );
                                            }
                                        }}
                                        disabled={!onDownloadFile}
                                        sx={{ p: 0.5 }}
                                    >
                                        <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ fontFamily: TABLE_FONT_FAMILY, fontStyle: 'italic', ml: 0.5, whiteSpace: 'nowrap' }}>
                                        {params.data.file_name}
                                    </Typography>
                                </Box>
                            );
                        }
                        return null;
                    },
                    comparator: hierarchyComparator
                },
                {
                    colId: 'type',
                    headerName: "Type",
                    field: "document_type_name",
                    width: 130,
                    comparator: hierarchyComparator,
                    cellRenderer: (params: any) => {
                        if (params.data.type === 'file' && params.data.file_category) {
                            let displayCategory = params.data.file_category;
                            if (params.data.file_category === 'main') displayCategory = 'Official PDF';
                            if (params.data.file_category === 'archive') displayCategory = 'Internal/Archive';
                            if (params.data.file_category === 'native') displayCategory = 'Native';
                            if (params.data.file_category === 'crs') displayCategory = 'CRS';
                            if (params.data.file_category === 'attachment') displayCategory = 'Attachment';

                            return (
                                <FileCategoryChip
                                    category={params.data.file_category}
                                    label={displayCategory}
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            );
                        }
                        return params.data.document_type_name;
                    }
                },
                {
                    colId: 'actions',
                    headerName: "Actions",
                    width: 100,
                    hide: viewMode === 'resources',
                    filter: false,
                    floatingFilter: false,
                    cellStyle: verticallyCenteredCellStyle,
                    cellRenderer: (params: any) => {
                        if (params.data.type === 'document') {
                            return (
                                <Box display="flex" alignItems="center" gap={0.5 * zoomLevel} height="100%">
                                    {!params.data.document_is_cancelled && (
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent row click
                                                openAddRevisionDialog(params.data.document_id);
                                            }}
                                            sx={{ p: 0.75 * zoomLevel }}
                                            title="Add Revision"
                                        >
                                            <AddIcon sx={{ fontSize: 20 * zoomLevel }} />
                                        </IconButton>
                                    )}
                                    {params.data.revision_name_step && params.data.status === 'approved' && onDownloadLatest && (
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadLatest(params.data.document_id, params.data.revision_id, params.data.revision_name_step);
                                            }}
                                            sx={{ p: 0.75 * zoomLevel }}
                                            title="Download Official Copy"
                                        >
                                            <FileDownloadIcon sx={{ fontSize: 20 * zoomLevel }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        } else if (params.data.type === 'revision') {
                            return (
                                <Box display="flex" alignItems="center" gap={0.5 * zoomLevel} height="100%">
                                    {params.data.status === 'approved' && onDownloadLatest && (
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadLatest(params.data.document_id, params.data.revision_id, params.data.revision_name_step);
                                            }}
                                            sx={{ p: 0.75 * zoomLevel }}
                                            title="Download Official Copy"
                                        >
                                            <FileDownloadIcon sx={{ fontSize: 20 * zoomLevel }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        }
                        return null;
                    }
                }
            ]
        },
        {
            groupId: 'revisionDetails',
            headerName: "Revision Details",
            openByDefault: true,
            children: [
                {
                    colId: 'rev',
                    headerName: "Status",
                    field: "revision_name_step",
                    width: 150,
                    cellStyle: verticallyCenteredCellStyle,
                    cellRenderer: (params: any) => {
                        if (params.data.revision_name_step) {
                            if (viewMode === 'resources' && params.data.type === 'revision') {
                                // Expandable revision
                                return (
                                    <Box display="flex" alignItems="center" height="100%">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleRevExpand(params.data.document_id, params.data.revision_id);
                                            }}
                                            sx={{ mr: 1 * zoomLevel, p: 0.5 * zoomLevel }}
                                        >
                                            {expandedRevIds.has(`${params.data.document_id}-${params.data.revision_id}`)
                                                ? <KeyboardArrowDown sx={{ fontSize: 16 * zoomLevel }} />
                                                : <KeyboardArrowRight sx={{ fontSize: 16 * zoomLevel }} />}
                                        </IconButton>
                                        <Chip
                                            label={params.value}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 'bold',
                                                height: 24 * zoomLevel,
                                                fontSize: `${0.75 * zoomLevel}rem`,
                                                '& .MuiChip-label': { px: 1 * zoomLevel }
                                            }}
                                        />
                                    </Box>
                                );
                            } else {
                                // Standard revision chip
                                return (
                                    <CellCenter>
                                        <Chip
                                            label={params.value}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 'bold',
                                                opacity: params.data.type === 'document' ? 0.9 : 1,
                                                height: 24 * zoomLevel,
                                                fontSize: `${0.75 * zoomLevel}rem`,
                                                '& .MuiChip-label': { px: 1 * zoomLevel }
                                            }}
                                        />
                                    </CellCenter>
                                );
                            }
                        }
                        return null;
                    },
                    sortable: false
                },
                {
                    colId: 'rev_counter',
                    headerName: "Rev #",
                    field: "revision_counter",
                    width: 90,
                    columnGroupShow: 'open',
                    cellRenderer: (params: any) => {
                        if (params.data.revision_counter !== undefined && params.data.revision_counter !== null) {
                            return <span>R{params.data.revision_counter}</span>;
                        }
                        return null;
                    },
                    sortable: false
                },
                {
                    colId: 'status',
                    headerName: "Rev Status",
                    field: "status",
                    width: 120,
                    columnGroupShow: 'open',
                    cellStyle: verticallyCenteredCellStyle,
                    cellRenderer: (params: any) => {
                        if (params.data.status) {
                            return (
                                <CellCenter>
                                    <StatusChip status={params.data.status} scale={zoomLevel} />
                                </CellCenter>
                            );
                        }
                        return null;
                    },
                    sortable: false
                },
                {
                    colId: 'exp_date',
                    headerName: "Expected Date",
                    field: "expected_issue_date",
                    width: 140,
                    columnGroupShow: 'open',
                    editable: (params) => params.data.type === 'revision',
                    valueFormatter: (params) => {
                        if (params.value) {
                            return new Date(params.value).toLocaleDateString();
                        }
                        return params.data.type === 'revision' ? '-' : '';
                    },
                    cellEditor: 'agDateCellEditor',
                    filter: 'agDateColumnFilter',
                    filterParams: dateFilterParams,
                    floatingFilter: true,
                    comparator: expectedDateComparator,
                    onCellValueChanged: (params) => {
                        if (params.newValue !== params.oldValue && params.data.revision_id) {
                            let dateStr = params.newValue;
                            if (params.newValue instanceof Date) {
                                dateStr = params.newValue.toISOString().split('T')[0];
                            }
                            onUpdateDate(params.data.document_id, params.data.revision_id, dateStr);
                        }
                    },
                },
                {
                    colId: 'issued_date',
                    headerName: "Issued Date",
                    field: "issued_date",
                    width: 140,
                    columnGroupShow: 'open',
                    valueFormatter: (params) => {
                        if (params.value) {
                            return new Date(params.value).toLocaleDateString();
                        }
                        return params.data.type === 'revision' ? '-' : '';
                    },
                    filter: 'agDateColumnFilter',
                    filterParams: dateFilterParams,
                    floatingFilter: true,
                    sortable: false
                },
                {
                    colId: 'workflow',
                    headerName: "Workflow",
                    field: "workflow",
                    flex: 1,
                    minWidth: 300,
                    hide: viewMode === 'resources',
                    autoHeight: true,
                    columnGroupShow: 'open',
                    filter: false,
                    floatingFilter: false,
                    cellStyle: verticallyCenteredCellStyle,
                    cellRenderer: (params: any) => {
                        if (params.data.workflow) {
                            return (
                                <CellCenter>
                                    <WorkflowSteps
                                        workflow={params.data.workflow}
                                        scale={zoomLevel}
                                    />
                                </CellCenter>
                            );
                        }
                        return '';
                    },
                    sortable: false
                }
            ]
        }
    ], [onUpdateDate, hierarchyComparator, expectedDateComparator, dateFilterParams, expandedDocIds, expandedRevIds, zoomLevel, viewMode, onDownloadLatest, contentColumnWidths, onDownloadFile]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true
    }), []);

    // Re-apply saved widths whenever columnDefs change (revision columns are
    // rebuilt from data), because AG Grid resets widths from columnDefs.
    useEffect(() => {
        if (!gridApi || !savedStateLoadedRef.current) {
            return;
        }
        if (hasSavedColumnWidthsRef.current) {
            applySavedColumnStateToApi(gridApi);
        }
        fitFlexibleColumnsToViewport(gridApi);
    }, [gridApi, columnDefs, contentColumnWidths, applySavedColumnStateToApi, fitFlexibleColumnsToViewport, savedStateVersion]);

    const onExport = useCallback(() => {
        // 1. Identify all unique revisions present in filteredData
        const allRevisions = new Set<string>();
        filteredData.forEach(doc => {
            doc.revisions.forEach(rev => {
                if (rev.revision_name_step) {
                    allRevisions.add(rev.revision_name_step);
                }
            });
        });

        // 2. Sort revisions
        const sortedRevisions = Array.from(allRevisions).sort((a, b) => {
            // Split "IFA 01" -> "IFA", "01"
            const partsA = a.split(' ');
            const partsB = b.split(' ');
            const nameA = partsA[0];
            const nameB = partsB[0];
            const stepA = partsA.length > 1 ? partsA[1] : '';
            const stepB = partsB.length > 1 ? partsB[1] : '';

            const idxA = revisionNames.findIndex(r => r.name === nameA);
            const idxB = revisionNames.findIndex(r => r.name === nameB);

            if (idxA !== idxB) {
                // If both found in revisionNames, sort by index
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                // If one not found, put it at the end
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
            }

            // Same name (or both unknown), compare step
            // Use numeric comparison for step
            return parseInt(stepA || '0') - parseInt(stepB || '0');
        });

        // 3. Construct Sheet
        const headerRow0: any[] = ['ND Number', 'Title', 'Type', 'WP', 'WU'];
        const headerRow1: any[] = ['', '', '', '', '']; // Placeholders for merge
        const merges: any[] = [];

        // Static columns merges (Row 0-1)
        for (let i = 0; i < 5; i++) {
            merges.push({ s: { r: 0, c: i }, e: { r: 1, c: i } });
        }

        let colIndex = 5;
        sortedRevisions.forEach(revStr => {
            // Header Row 0: Revision Name (merged 3 cells)
            headerRow0.push(revStr);
            headerRow0.push('');
            headerRow0.push('');
            merges.push({ s: { r: 0, c: colIndex }, e: { r: 0, c: colIndex + 2 } });

            // Header Row 1: Sub-headers
            headerRow1.push('Status');
            headerRow1.push('Exp. Date');
            headerRow1.push('Issued Date');

            colIndex += 3;
        });

        // 4. Data Rows
        const dataRows = filteredData.map(doc => {
            const row: any[] = [
                doc.document_nd_number,
                doc.document_title,
                doc.document_type_name,
                doc.work_package_code,
                doc.work_unit_code
            ];

            sortedRevisions.forEach(revStr => {
                // Find revision
                const rev = doc.revisions.find(r => r.revision_name_step === revStr);
                if (rev) {
                    row.push(rev.status || '');
                    // For dates, check if valid date string
                    row.push(rev.expected_issue_date ? new Date(rev.expected_issue_date).toLocaleDateString() : '');
                    row.push(rev.issued_date ? new Date(rev.issued_date).toLocaleDateString() : '');
                } else {
                    row.push('');
                    row.push('');
                    row.push('');
                }
            });
            return row;
        });

        const wsData = [headerRow0, headerRow1, ...dataRows];
        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet['!merges'] = merges;

        // Auto-width (basic)
        const wscols = [];
        for (let i = 0; i < colIndex; i++) wscols.push({ wch: 15 });
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MDR Export');
        XLSX.writeFile(workbook, `mdr_export_${new Date().toISOString().split('T')[0]}.xlsx`);

    }, [filteredData, revisionNames]);

    return (
        <Box sx={{ flex: 1, height: '100%', minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0} flexWrap="wrap" useFlexGap>
                {showProjectFilter && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Project</InputLabel>
                        <Select
                            value={selectedProjectId}
                            label="Project"
                            onChange={(e) => setSelectedProjectId(e.target.value as number | 'all')}
                            MenuProps={getZoomCompensatedSelectMenuProps()}
                        >
                            <MenuItem value="all">All Projects</MenuItem>
                            {projects.map(project => (
                                <MenuItem key={project.id} value={project.id}>
                                    {project.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    size="small"
                    placeholder="Quick filter..."
                    value={quickFilter}
                    onChange={(e) => setQuickFilter(e.target.value)}
                    sx={{ flex: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
                {columnFiltersActive && (
                    <ClearGridFiltersButton onClick={() => clearColumnFilters(gridApi)} />
                )}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UnfoldMoreIcon />}
                    onClick={expandAll}
                >
                    Expand All
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UnfoldLessIcon />}
                    onClick={collapseAll}
                >
                    Collapse All
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ViewColumnIcon />}
                    onClick={(e) => {
                        setColVisAnchorEl(e.currentTarget);
                        if (gridApi) {
                            const cols = gridApi.getAllGridColumns();
                            if (cols) {
                                setAllColumns(cols.map(c => ({
                                    colId: c.getColId(),
                                    headerName: c.getColDef().headerName || c.getColId(),
                                    visible: c.isVisible()
                                })));
                            }
                        }
                    }}
                >
                    Columns
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={onExport}
                >
                    Export XLSX
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ZoomInIcon />}
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 1.5}
                >
                    Zoom In
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ZoomOutIcon />}
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.7}
                >
                    Zoom Out
                </Button>
            </Stack>

            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    '& .ag-root-wrapper': {
                        fontSize: `${zoomLevel}rem`
                    },
                    '& .ag-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                    },
                    '& .ag-cell-value': {
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        lineHeight: 'normal',
                    },
                }}
                className={theme.palette.mode === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
            >
                <AgGridReact
                    theme="legacy"
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.id}
                    rowHeight={45 * zoomLevel}
                    headerHeight={45 * zoomLevel}
                    floatingFiltersHeight={45 * zoomLevel}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    onGridReady={onGridReady}
                    onGridSizeChanged={(params) => {
                        if (!savedStateLoadedRef.current) {
                            return;
                        }
                        scheduleGridResizeLayout(params.api);
                    }}
                    onFirstDataRendered={(params) => {
                        if (!savedStateLoadedRef.current) {
                            return;
                        }
                        fitFlexibleColumnsToViewport(params.api);
                    }}
                    onColumnResized={(event: ColumnResizedEvent) => {
                        if (
                            !event.finished ||
                            !event.source ||
                            PROGRAMMATIC_COLUMN_RESIZE_SOURCES.has(event.source)
                        ) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onColumnMoved={(event: ColumnMovedEvent) => {
                        if (!event.finished) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onColumnVisible={(event: ColumnVisibleEvent) => {
                        if (event.source === 'api' || isApplyingSavedStateRef.current) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onFilterChanged={(event: FilterChangedEvent) => {
                        syncColumnFilters(event.api);
                        if (!restoreCompleteRef.current) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onColumnGroupOpened={(event) => {
                        persistTableState(event.api);
                    }}
                    onRowClicked={(event) => {
                        // Check if text is selected - if so, don't trigger row click
                        const selection = window.getSelection();
                        if (selection && selection.toString().length > 0) {
                            return;
                        }

                        if (!onRowClick) return;

                        if (event.data.type !== 'document') {
                            onRowClick(null);
                            return;
                        }

                        const doc = data.find(d => d.document_id === event.data.document_id);
                        if (doc) {
                            onRowClick(doc);
                        }
                    }}
                    onRowDoubleClicked={(event) => {
                        const doc = data.find(d => d.document_id === event.data.document_id);
                        if (doc && onRowDoubleClick) {
                            onRowDoubleClick(doc);
                        }
                    }}
                    getRowStyle={(params) => {
                        if (params.data.type === 'document') {
                            return {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                fontWeight: 500,
                                textDecoration: params.data.document_is_cancelled ? 'line-through' : 'none',
                                opacity: params.data.document_is_cancelled ? 0.75 : 1
                            };
                        }
                        if (params.data.type === 'file') {
                            return { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.005)', fontStyle: 'italic' };
                        }
                        return {};
                    }}
                />
            </Paper>

            <Popover
                open={Boolean(colVisAnchorEl)}
                anchorEl={colVisAnchorEl}
                onClose={() => setColVisAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto', minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Show/Hide Columns</Typography>
                    <FormGroup>
                        {allColumns.map(col => (
                            <FormControlLabel
                                key={col.colId}
                                control={
                                    <Checkbox
                                        checked={col.visible}
                                        onChange={(e) => toggleColumn(col.colId, e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={col.headerName}
                            />
                        ))}
                    </FormGroup>
                </Box>
            </Popover>



            {/* Add Revision Dialog */}
            {addRevDialogOpen && addRevDocId && (
                <RevisionWithWorkflowDialog
                    open={addRevDialogOpen}
                    onClose={() => setAddRevDialogOpen(false)}
                    projectId={selectedDocForRev?.project_id ?? 0}
                    documentId={addRevDocId}
                    documentTitle={selectedDocForRev?.document_title || ''}
                    revisionNames={revisionNames}
                    parties={parties}
                    onSubmit={handleAddRevisionSave}
                    nextRevisionCounter={getNextRevisionCounter(selectedDocForRev?.revisions ?? [])}
                />
            )}
        </Box >
    );
};

export default MDRTableAgGrid;
